
import { useState, useEffect, useCallback } from "react";

interface PreviewData {
  title?: string;
  description?: string;
  items?: Array<any>;
  columns?: Array<{ key: string; label: string }>;
  [key: string]: any;
}

interface UseFetchPreviewResult {
  previewData: PreviewData | null;
  isLoading: boolean;
  previewError: string | null;
  isCorsError: boolean;
  fetchPreviewData: () => Promise<void>;
  showPreview: boolean;
  setShowPreview: React.Dispatch<React.SetStateAction<boolean>>;
  togglePreview: () => void;
}

export const useFetchPreview = (url: string): UseFetchPreviewResult => {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isCorsError, setIsCorsError] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(true);

  const fetchPreviewData = useCallback(async () => {
    if (!url || url.trim() === "") {
      setPreviewError("יש להזין כתובת URL חוקית");
      setPreviewData(null);
      return;
    }

    setIsLoading(true);
    setPreviewError(null);
    setIsCorsError(false);
    
    try {
      // First try with standard fetch
      try {
        const response = await fetch(url, {
          headers: { 
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Origin': window.location.origin
          },
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`שגיאת שרת: ${response.status}`);
        }
        
        const jsonData = await response.json();
        
        // Format the data - if it's an array, format each item
        let formattedData: PreviewData;
        
        if (Array.isArray(jsonData)) {
          // Format each item to have title and description
          const formattedItems = jsonData.map(item => ({
            ...item,
            title: item.type || "",
            description: item.lesson || ""
          }));
          
          formattedData = {
            title: "לימוד יומי",
            description: "שיעורים יומיים",
            items: formattedItems
          };
        } else {
          // If it's an object with items array
          if (jsonData.items && Array.isArray(jsonData.items)) {
            const formattedItems = jsonData.items.map(item => ({
              ...item,
              title: item.type || "",
              description: item.lesson || ""
            }));
            
            formattedData = {
              ...jsonData,
              items: formattedItems
            };
          } else {
            // Just regular object
            formattedData = jsonData;
          }
        }
        
        setPreviewData(formattedData);
        
      } catch (initialError) {
        // If it's potentially a CORS error, try with no-cors mode
        if (initialError instanceof TypeError && initialError.message.includes('fetch')) {
          console.log("Initial fetch failed, trying with no-cors mode");
          setIsCorsError(true);
          
          // With no-cors, we can't actually read the response data
          // But we can show that a response was received
          await fetch(url, { mode: 'no-cors' });
          
          // Create a placeholder preview for the UI
          setPreviewData({
            title: "נתונים מהשרת",
            description: "לא ניתן להציג תצוגה מקדימה בגלל מגבלות CORS",
            items: []
          });
          
          setPreviewError("אזהרת CORS: ניתן לשמור את ההגדרות, אך יש לעדכן את הגדרות CORS בפונקציית ה-Edge של Supabase כדי להציג את הנתונים כראוי");
        } else {
          // If it's not a CORS issue, rethrow
          throw initialError;
        }
      }
    } catch (error) {
      console.error("Error fetching preview data:", error);
      
      const errorMessage = error instanceof Error ? error.message : "שגיאה לא ידועה";
      
      // Check if this might be a CORS error
      if (error instanceof TypeError && errorMessage.includes('fetch')) {
        setIsCorsError(true);
        setPreviewError(`שגיאת CORS: אין הרשאת גישה לנתונים מהדפדפן. יש לעדכן את הגדרות CORS בפונקציית Edge של Supabase`);
      } else {
        setPreviewError(`שגיאה בטעינת נתונים: ${errorMessage}`);
      }
      
      setPreviewData(null);
    } finally {
      setIsLoading(false);
    }
  }, [url]);

  const togglePreview = useCallback(() => {
    setShowPreview(prev => !prev);
  }, []);

  // Fetch preview data when URL changes or when user toggles preview on
  useEffect(() => {
    if (url && showPreview) {
      fetchPreviewData();
    }
  }, [url, showPreview, fetchPreviewData]);

  return {
    previewData,
    isLoading,
    previewError,
    isCorsError,
    fetchPreviewData,
    showPreview,
    setShowPreview,
    togglePreview
  };
};
